import { attachAcquisition, recordFirstTouch, type FirstTouchPayload } from "./api";

const ANONYMOUS_ID_KEY = "chatcode.anonymous_id";
const FIRST_TOUCH_RECORDED_KEY = "chatcode.first_touch_recorded";

export function getAnonymousId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const existing = window.localStorage.getItem(ANONYMOUS_ID_KEY);
    if (existing) return existing;
    const created = `anon_${crypto.randomUUID().replace(/-/g, "")}`;
    window.localStorage.setItem(ANONYMOUS_ID_KEY, created);
    return created;
  } catch {
    return null;
  }
}

export function recordBrowserFirstTouchOnce(): void {
  if (typeof window === "undefined") return;
  const anonymousId = getAnonymousId();
  if (!anonymousId) return;
  try {
    if (window.localStorage.getItem(FIRST_TOUCH_RECORDED_KEY) === "1") return;
  } catch {
    return;
  }

  const payload = firstTouchPayload(anonymousId);
  void recordFirstTouch(payload)
    .then(() => {
      try {
        window.localStorage.setItem(FIRST_TOUCH_RECORDED_KEY, "1");
      } catch {
        // Best-effort attribution must not affect app boot.
      }
    })
    .catch(() => {
      // Retry on the next app boot. Product flows must never wait on analytics.
    });
}

export function attachBrowserAcquisition(): void {
  const anonymousId = getAnonymousId();
  void attachAcquisition(anonymousId).catch(() => {
    // Attribution attach is recoverable and can be retried on a later app load.
  });
}

function firstTouchPayload(anonymousId: string): FirstTouchPayload {
  const params = new URLSearchParams(window.location.search);
  const source = params.get("utm_source") || sourceFromReferrer(document.referrer);
  return {
    anonymous_id: anonymousId,
    source,
    medium: params.get("utm_medium"),
    campaign: params.get("utm_campaign"),
    term: params.get("utm_term"),
    content: params.get("utm_content"),
    landing_url: window.location.href,
    referrer: document.referrer || null,
    telegram_start_param: params.get("tgWebAppStartParam") || params.get("startapp"),
    partner_key: params.get("partner") || params.get("ref"),
    consent: {
      // Until a marketing consent banner lands, do not store ad click ids for
      // regions where server-side consent gating requires explicit consent.
      ad_storage: false,
    },
    ad_ids: {
      gclid: params.get("gclid") || undefined,
      gbraid: params.get("gbraid") || undefined,
      wbraid: params.get("wbraid") || undefined,
      msclkid: params.get("msclkid") || undefined,
      fbclid: params.get("fbclid") || undefined,
    },
  };
}

function sourceFromReferrer(referrer: string): string | null {
  if (!referrer) return "direct";
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, "");
    if (!host) return "referral";
    if (host.includes("google.")) return "google";
    if (host.includes("t.me") || host.includes("telegram.")) return "telegram";
    if (host.includes("github.")) return "github";
    return host;
  } catch {
    return "referral";
  }
}
